// @ts-strict-ignore

/**
 * @module Calls
 */

import { Subject, Subscription, filter, interval } from "rxjs";
import { MainService } from "rainbow-web-sdk/services";
import { RBEvent } from "./event.model";
import { ConversationServiceRB } from "rainbow-web-sdk/services/conversation/conversation.service";
import { CallServiceRB } from "rainbow-web-sdk/services/call/call.service";
import { Conversation } from "./conversation.model";
import { User } from "./user.model";
import { Contact } from "./contact.model";
import { CALL_RECORDER_SVC } from "../services/call/callRecorder.service";

/**
 * Call events send to other services
 * Can listen to it via `Call.subscribe()` API.
 */
export enum CallEvents {

    /**
     * @eventProperty
     * This `RBEvent` is sent when the call capabilities have been updated.<br>
     * The new call capabilities can be retrieved via the 'capabilities' property.
     */
    ON_CALL_CAPABILITIES_UPDATED = "ON_CALL_CAPABILITIES_UPDATED",

    /**
     * @eventProperty
     * This `RBEvent` is sent when the status of the call (from a telephone point of view) has changed.<br>
     * The new call status can be retrieved via the 'callStatus' property.
     */
    ON_CALL_STATUS_CHANGE = "ON_CALL_STATUS_CHANGE",

    /**
     * @eventProperty
     * This `RBEvent` is sent when the status of the mute of the call (On or Off) has changed.<br>
     * The new mute status can be retrieved via the 'isMuted' property.
     */
    ON_CALL_MUTE_CHANGE = "ON_CALL_MUTE_CHANGE",

    /**
     * @eventProperty
     * This `RBEvent` is sent when during a call, the network quality has changed.
     *
     * **Event Data**
     * - **data**: The current network quality for the call
     */
    ON_CALL_NETWORK_QUALITY_CHANGE = "ON_CALL_NETWORK_QUALITY_CHANGE",

    /**
     * @internal
     * @eventProperty
     * This `RBEvent` is sent when a bad network quality has been detected.<br>
     * The detection is currently done by Web Client component, based on internal WebClient criteria for UI purpose.
     *
     * **Event Data**
     * - **data**: The current network quality for the call
     */
    ON_CALL_NETWORK_BAD_QUALITY = "ON_CALL_NETWORK_BAD_QUALITY",

    /**
     * @eventProperty
     * This `RBEvent` is sent when there have been changes in the media used by the local or the remote user.<br>
     * By change we mean the addition or removal of a media (Video/Sharing) for the current call.
     */
    ON_CALL_MEDIA_UPDATED = "ON_CALL_MEDIA_UPDATED",

    /**
     * @eventProperty
     * This `RBEvent` is sent when when the call has evolved and as a result some of its properties, such as its status, have been updated.
     */
    ON_CALL_UPDATED_EVENT = "ON_CALL_UPDATED_EVENT",

    /**
    * @eventProperty
    * This `RBEvent` is sent when the distant party has started / stopped the recording of the call; Only available for direct P2P calls with Rainbow user
    */
    ON_CALL_REMOTE_RECORDING_UPDATE = "ON_CALL_REMOTE_RECORDING_UPDATE",

    /**
     * @eventProperty
     * This `RBEvent` is sent when the call recorder state change
     * **Event Data**
     * - **callRecorderState**: The new CallRecorderState of the call
     */
    ON_CALL_RECORDING_UPDATE = "ON_CALL_RECORDING_UPDATE"
}

/**
 * Media type available in a P2P call.<br>
 * Media can be combined within the same call, e.g. for a video call, both audio and video media will be used.
 */
export enum MediaType {
    /**
     * Audio
     */
    AUDIO = "audio",

    /**
     * Video
     */
    VIDEO = "video",

    /**
     * Screen Sharing
     */
    SHARING = "sharing"
}

/**
 * Type of call managed for a P2P communication.
 */
export enum CallType {
    /**
     * Webrtc call
     */
    WEBRTC = 1,

    /**
     * Phone call
     */
    PHONE = 2
}

/**
 * The possible status of the call (e.g. dialing, active, releasing...)
 */
export enum CallStatus {
    /**
     * Call Cleared
     */
    ENDED = "ENDED",

    /**
     * In dialing
     */
    DIALING = "DIALING",

    /**
     * In queue (user side)
     */
    QUEUED_INCOMMING = "QUEUED_INCOMMING",

    /**
     * In queue (recipient side)
     */
    QUEUED_OUTGOING = "QUEUED_OUTGOING",

    /**
     * Call in ringing (user side)
     */
    RINGING_INCOMMING = "RINGING_INCOMMING",

    /**
     * Call in ringing (recipient side)
     */
    RINGING_OUTGOING = "RINGING_OUTGOING",

    /**
     * Active call
     */
    ACTIVE = "ACTIVE",

    /**
     * Call on hold by local party
     */
    HOLD = "HOLD",

    /**
     * Call put on hold by the distant party
     */
    PUT_ON_HOLD = "PUT_ON_HOLD",

    /**
     * Call is clearing
     */
    RELEASING = "RELEASING",

    /**
     * Call is being answered
     */
    ANSWERING = "ANSWERING",

    /**
     * Trying to connect / reconnect the call
     */
    CONNECTING = "CONNECTING",

    /**
     * Call error
     */
    ERROR = "ERROR"
}

/**
 * Call direction
 */
export enum CallDirection {
    /**
     * Default initial value.
     */
    UNDEFINED = "",

    /**
     * Incoming call.<br>
     * Call in which the connected user is the call recipient.
     */
    INCOMING = "incoming",

    /**
     * Outgoing call.<br>
     * Call in which the connected user initiates the call.
     */
    OUTGOING = "outgoing"
}

/**
 * Call Redirection cause
 */
export enum CallRedirectionCause {
    /**
     * Default initial value.
     */
    UNDEFINED = "",

    /**
     * The call has been forwarded.
     */
    FORWARDED = "forwarded",

    /**
     * The call is a group call : it is redirected by the group number to the group member.
     */
    HUNTGROUP = "hg"
}

/**
 * @internal
 * Media used by the local or the remote party of a WebRTC or PBX call
 */
export const CallMedia = {
    /**
     * No media
     */
    NONE: 0,

    /**
     * WebRTC audio call
     */
    AUDIO: 1,

    /**
     * WebRTC audio and video call
     */
    VIDEO: 2,

    /**
     * Telephony call (PBX) call
     */
    PHONE: 4,

    /**
     * Screen Sharing
     */
    SHARING: 8,
} as const;

/**
 * For a call, indicates for each managed media whether it is in use or not.
 */
export interface CallMedias {
    /**
     * Indicates if the audio stream is used in the call.
     */
    audio?: boolean;

    /**
     * Indicates if the video stream is used in the call.<br>
     * This stream is active for example when using a camera.
     */
    video?: boolean;

    /**
     * Indicates if the sharing stream is used in the call.<br>
     * This stream is used for example when sharing a browser tab, a window or the entire screen.
     */
    sharing?: boolean;
}

/**
 * Data associated with the destination of a call.
 */
export interface CallDestination {
    /**
     * Peer object related to the Call destination.<br>
     * If the call destination is a Rainbow user, the object is an instance of User.
     */
    user?: User;

    /**
     * Phone number to be used for the call destination.<br>
     * This can be a telephone number associated with the Rainbow user profile.<br>
     * Or in the case of an external destination, a public PSTN number.
     */
    phoneNumber?: string;

    /**
     * Phone number country.<br>
     * This information is only relevant if the destination is external.<br>
     * If not indicated, the caller's country is taken into account.
     */
    country?: string;
}

/**
 * Data associated with the initial called party
 */
export interface CalledParty {

    /**
     * Pbx call's initial called number
     * @readonly
     */
    phoneNumber: string;

    /**
     * Pbx call's initial called display name
     * @readonly
     */
    displayName: string;

    /**
     * Pbx call's initial called lastname
     * @readonly
     */
    lastName: string;

    /**
     * Pbx call's initial called firstname
     * @readonly
     */
    firstName: string;

    /**
     * Pbx call's initial called Jabber identifier
     * @readonly
     */
    jid: string;

    /**
     * Pbx call's initial called role : "called" for forwarded calls / "called_group" for group calls
     * @readonly
     */
    role: string;
}

/**
 * Call capabilities representing all the actions that could be done for the call given the state
 */
export interface CallCapabilities {
    /**
     * Indicates if the incoming call can be answered.
     * @readonly
     */
    answer: boolean;

    /**
     * Indicates if the call can be released / ended / declined.
     * @readonly
     */
    release: boolean;

    /**
     * Indicates if we can deflect the incoming call to the VoiceMail.<br>
     * Only available for HYBRID systems (for Cloud telephony, the release action will follow the system/user configuration)
     * @readonly
     */
    deflectToVoicemail: boolean;

    /**
     * Indicates if we can deflect the incoming call to a phone number.<br>
     * The possible destinations numbers for call deflection are available in the 'deflectPhoneNumberDestinations' data.
     * @readonly
     */
    deflectToPhoneNumber: boolean;

    /**
     * The list of phone numbers where we can deflect the call
     * @readonly
     */
    deflectPhoneNumberDestinations: [{ id: string; number: string; }?];

    /**
     * Indicates if we can mute the call
     * @readonly
     */
    mute: boolean;

    /**
     * Indicates if we can unmute the call
     * @readonly
     */
    unmute: boolean;

    /**
     * Indicates if we can add the audio media to the call.<br>
     * This could happen if there's no audio, for example in case of sharing only call.
     * @readonly
     */
    addAudio: boolean;

    /**
     * Indicates if we can add the video media to the call.
     * @readonly
     */
    addVideo: boolean;

    /**
     * Indicates if we can remove the video media from the call.
     * @readonly
     */
    removeVideo: boolean;

    /**
     * Indicates if we can add the sharing media to the call.
     * @readonly
     */
    addSharing: boolean;

    /**
     * Indicates if we can remove the sharing media from the call.
     * @readonly
     */
    removeSharing: boolean;

    /**
     * Indicates if we can make a blind transfer of this call (transfer it to some other contact without having a call with them beforehand).<br>
     * This will remove the current call, and the other party will be transferred to the other contact / number.
     * @readonly
     */
    blindTransfer: boolean;

    /**
     * Indicates if we can make a conference call from the current call and one other call in progress for the current user (can be used to merge the 2 calls).<br>
     * This will remove the 2 calls and create one single call (for hybride systems) or web conference.
     * @readonly
     */
    mergeCalls: boolean;

    /**
     * Indicates if we can make a conference call from the current call by adding any other contact or number (that we dont have currently call with).<br>
     * This will remove the current call and create a web conference call instead.
     * @readonly
     */
    addParticipantsToCall: boolean;

    /**
     * Indicates if we can put the call on hold.
     * @readonly
     */
    hold: boolean;

    /**
     * Indicates if we can retrieve the call (if it is on hold).
     * @readonly
     */
    retrieve: boolean;

    /**
     * Indicates if we can send dtmf for the call.
     * @readonly
     */
    dtmf: boolean;

    /**
     * Indicates if we can transfer the current call to the other call.<br>
     * If the call is on HOLD, then the tranfer is always made towards the ACTIVE call.<br>
     * Otherwise, we can transfer this call to any other call from the transferCallDestinations list.
     * @readonly
     */
    transferToOtherCall: boolean;

    /**
     * The list of calls where we can transfer this call;
     * @readonly
     */
    transferCallDestinations: Call[];

    /**
    * Indicates whether recording can be started for this call
    */
    startRecording: boolean;

    /**
    * Indicates whether recording can be stopped for this call
    */
    stopRecording: boolean;

    /**
    * Indicates whether recording can be paused for this call
    */
    pauseRecording: boolean;

    /**
    * Indicates whether recording can be resumed for this call
    */
    resumeRecording: boolean;

    /**
     * Indicates whether recording can be cancelled for this call
    */
    cancelRecording: boolean;
}

export interface Call {
    /**
     * Capabilities of the call
     * @readonly
     */
    capabilities: CallCapabilities;

    /**
     * Call telephone status
     * @readonly
     */
    callStatus: CallStatus;

    /**
     * The type of the call (WebRTC or Telephony)
     * @readonly
     */
    callType: CallType;

    /**
     * On an incoming call, contains the subject of the call if this has been specified by the caller
     * @readonly
     */
    subject: string;

    /**
     * The user reference representing the other party (as caller or callee) engaged in this call
     * @readonly
     */
    contact: User;

    /**
     * Call duration in seconds
     * @readonly
     */
    duration: number;

    /**
     * Call direction
     * This information is relevant only for telephone (PBX) call. For WebRTC call it is undefined.
     */
    direction: CallDirection;

    /**
     * The media stream used by the local user.
     * Typed as number BUT MUST follow the values defined by 'CallMedia' type or a combination thereof.
     * @readonly
     */
    localMedias: CallMedias;

    /**
     * The media stream used by the remote user/party.
     * Typed as number BUT MUST follow the values defined by 'CallMedia' type or a combination thereof.
     * @readonly
     */
    remoteMedias: CallMedias;

    /**
     * The current mute status of the call
     * @readonly
     */
    isMuted: boolean;

    /**
     * The current network quality (0 - bad, 1 - medium, 2 - good) for the call
     * @readonly
     */
    networkQuality: number;

    /**
     * The original unprocessed video stream from the camera.
     * This stream contains the raw video before any processing is applied, such as virtual background effects or filters.
     * It is used as a reference when video effects need to be applied or removed during the call.
     * @internal
     */
    originalStream: MediaStream | null;

    /**
     * Subscribe to updates on the current call (all events are of RBEvent<CallEvents>
     * @param callback - The callback function that will be called when events are received
     * @param eventNames - (optional) array of event to listen
     * @returns Returns RxJS Subscription
     */
    subscribe(callback: (event: RBEvent<CallEvents>) => any, eventNames?: CallEvents | CallEvents[]): Subscription;

    /**
     * Answer the current incoming call.<br>
     * This feature can be used only if the "answer" call capability is 'True'.<br>
     * By default, the answer is done with AUDIO only.<br>
     * If the 'withVideo' option is set to 'True' and the incoming call contains video, the answer will directly be done with audio and video.
     * @param withVideo - Option to answer the call directly with audio and video. Default value is 'False'
     */
    answer(withVideo?: boolean): Promise<void>;

    /**
     * Release the current call.<br>
     * This method allows to decline an incoming call or stop an outgoing call.<br>
     * This feature can be used only if the "release" call capability is 'True'.
     */
    release(): Promise<void>;

    /**
     * Put on hold the current call.<br>
     * This feature can be used only if the "hold" call capability is 'True'.
     */
    hold(): Promise<void>;

    /**
     * Retrieve from hold the current call.<br>
     * This feature can be used only if the "retrieve" call capability is 'True'.
     */
    retrieve(): Promise<void>;

    /**
     * Mute the current call.<br>
     * This feature can be used only if the "mute" call capability is 'True'.
     */
    mute(): void;

    /**
     * Unmute the current call.<br>
     * This feature can be used only if the "unmute" call capability is 'True'.
     */
    unmute(): void;

    /**
     * Send DTMF signal to the current call.<br>
     * This feature can be used only if the "dtmf" call capability is 'True'.
     * @param dialKey - The DTMF character sequences to be send
     * Only the following characters are authorised: the ten single digits (0..9), the letters (a,b,c,d), the asterisk (*) and the pound sign (#).
     */
    sendDTMF(dialKey: string): void;

    /**
     * Add the AUDIO media to the current call.<br>
     * This feature can be used only if the "addAudio" call capability is 'True' and available only for WebRTC Calls.
     */
    addAudio(): Promise<void>;

    /**
     * Add the VIDEO media to the current call.<br>
     * This feature can be used only if the "addVideo" call capability is 'True' and available only for WebRTC Calls.
     */
    addVideo(): Promise<void>;

    /**
     * Add the SHARING media to the current call.<br>
     * This feature can be used only if the "addSharing" call capability is 'True' and available only for WebRTC Calls.
     */
    addSharing(): Promise<void>;

    /**
     * Remove the VIDEO media from the current call.<br>
     * This feature can be used only if the "removeVideo" call capability is 'True' and available only for WebRTC Calls.
     */
    removeVideo(): Promise<void>;

    /**
     * Remove the SHARING media from the current call.<br>
     * This feature can be used only if the "removeSharing" call capability is 'True' and available only for WebRTC Calls.
     */
    removeSharing(): Promise<void>;

    /**
     * Add participants to the current call.<br>
     * This feature can be used only if the "addParticipantsToCall" call capability is 'True'.<br>
     * The participants could be other Rainbow users or phone numbers (if the PBX system supports it).<br>
     * This will result in a new conversation being created with web conference inside. The active call will be "transferred" to the bubble conference;
     * @param participants - The list of participants to be added to the call
     * @returns The new bubble conversation where the web conference is active
     */
    addParticipantsToCall(participants: CallDestination[]): Promise<Conversation>;

    /**
     * Create a conference from 2 calls, including the current call.
     * This will lead to the creation of either a web conference (managed via a Rainbow bubble) or a pure telephone conference (managed by a PBX system).
     * In the first case, a new conversation is created with the web conference inside. The two calls will be "transferred" to the bubble conference.
     * In the second case, a new 'conference' type conversation is created. This applies only with a Rainbow Hybrid (OXO or OXE) telephone system.
     * This feature can be used only if the "mergeCalls" call capability is 'True'.
     * @returns The new conversation or null depending on the type of conference created
     */
    activateConference(): Promise<Conversation | undefined>;

    /**
     * Blind transfer the current call another destination which could be any rainbow user or phone number.<br>
     * A blind transfer is when you transfer a call directly to another party without consulting with the person receiving the call.<br>
     * In other words, there is no need to call the other party to make the transfer.<br>
     * This feature is available only if the connected user is behind a Rainbow Hub telephone system.
     * @param destination - The destination of the blind transfer
     */
    blindTransfer(destination: CallDestination): Promise<void>;

    /**
     * Deflect the current incoming call to the voice mail.<br>
     * This feature can be used only if the "deflectToVoicemail" call capability is 'True'.<br>
     * The 'deflect to voice mail' is only available on HYBRID telephony systems.<br>
     * For Cloud telephony systems, the release call will follow the user configuration (which could be deflect or other).
     */
    deflectToVoicemail(): Promise<void>;

    /**
     * Deflect (redirect) the current incoming call to the specified phone number.<br>
     * This feature can be used only if the "deflectToPhoneNumber" call capability  is 'True'.<br>
     * The list of phone numbers where we can deflect the incoming call is available in the 'deflectPhoneNumberDestinations' call capability.
     * @param number - The phone number the call will be deflected to.
     */
    deflectToPhoneNumber(number: string): Promise<void>;

    /**
     * Transfer the current call to another call.<br>
     * If the destination call is not specified, then the transfer will be done towards the current active call.<br>
     * The list of destination calls to which we can deflect the call current is available in the 'transferCallDestinations' call capability.
     * @param destinationCall - The destination call to which we want to transfer the current call.
     */
    transferToOtherCall(destinationCall?: Call): Promise<void>;

    /**
     * Attach the local video stream to a given HTML VIDEO tag element;
     * @param HtmlElementId - The ID of the HTML VIDEO tag element visible on the page
     */
    attachLocalVideoStream(HtmlElementId: string): void;

    /**
     * Attach the local sharing stream to a given HTML VIDEO tag element;
     * @param HtmlElementId - The ID of the HTML VIDEO tag element visible on the page
     */
    attachLocalSharingStream(HtmlElementId: string): void;

    /**
     * Attach the remote video stream to a given HTML VIDEO tag element;
     * @param HtmlElementId - The ID of the HTML VIDEO tag element visible on the page
     */
    attachRemoteVideoStream(HtmlElementId: string): void;

    /**
     * Attach the remote sharing stream to a given HTML VIDEO tag element;
     * @param HtmlElementId - The ID of the HTML VIDEO tag element visible on the page
     */
    attachRemoteSharingStream(HtmlElementId: string): void;

    /**
    * @internal
    */
    toJSON(): any;

    /**
     * This API allows the user to start the recording of the current call.
     */
    startRecording(): Promise<void>;

    /**
     * This API allows the user to stop the recording of the current call.
     */
    stopRecording(): Promise<void>;

    /**
     * This API allows the user to pause the recording of the current call.
     */
    pauseRecording(): void;

    /**
     * This API allows the user to resume the recording of the current call.
     */
    resumeRecording(): void;

    /**
     * This API allows the user to get the duration of the current call recording.
     */
    getRecordingDuration(): number;

    /**
     * This API allows the user to cancel the recording of the current call, without downloading the recording file.
     */
    cancelRecording(): void;
}

/**
 * This CallRecordingState enum lists all the states a record can be in.
*/
export enum CallRecordingState {
    /**
     * This 'RECORDING_IN_PROGRESS' state means that the recording is in progress.
    */
    RECORDING_IN_PROGRESS = "RECORDING_IN_PROGRESS",

    /**
     * This 'RECORDING_PAUSED' state means that the recording is on pause.
    */
    RECORDING_PAUSED = "RECORDING_PAUSED",

    /**
     * This 'RECORDING_STOPPED' state means that the recording is stopped.
    */
    RECORDING_STOPPED = "RECORDING_STOPPED",
}

/**
 * @internal
 * This class represents a call. <br>
 * A call is a "short" interaction between the user and one or several contacts (Rainbow users or not) that uses either a WebRTC media (audio, video or screen sharing) or a Telephony media (PBX phone). <br>
 * No Rainbow users such as external users can only be engaged into a Telephony call. <br>
 * A call is always attached to a conversation.
 */
export class CallRB implements Call {

    // -----    DEFINITION KEPT FOR DESKTOP APP COMPATIBILITY   -----
    /**
     * @deprecated OBSOLETE, TO BE REMOVED WHEN DESKTOP REMOVES CODE
     */
    public static Status = {
        UNKNOWN: { key: 0, value: "Unknown" },
        DIALING: { key: 1, value: "dialing" },
        QUEUED_INCOMMING: { key: 2, value: "queuedIncomming" },
        QUEUED_OUTGOING: { key: 10, value: "queuedOutGoing" },
        RINGING_INCOMMING: { key: 3, value: "incommingCall" },
        RINGING_OUTGOING: { key: 4, value: "ringingOutgoing" },
        ACTIVE: { key: 5, value: "active" },
        HOLD: { key: 6, value: "held" },
        PUT_ON_HOLD: { key: 7, value: "putOnHold" },
        RELEASING: { key: 8, value: "releasing" },
        ANSWERING: { key: 9, value: "answering" },
        CONNECTING: { key: 12, value: "connecting" },
        ERROR: { key: 11, value: "error" },
        ENDED: { key: 0, value: "Unknown" }
    };

    /**
     * NOT TO BE USED ANYMORE !!!! Use callStatus instead !
     * @deprecated -- Use {@link callStatus} instead !
     */
    public status: any;

    /**
     * @deprecated OBSOLETE, TO BE REMOVED WHEN DESKTOP REMOVES CODE
     */
    public static Type = {
        /** WebRTC call */
        WEBRTC: { key: 1, value: "Video" },
        /** Telephony call (from PBX) */
        PHONE: { key: 2, value: "Phone" }
    };

    /**
     * @deprecated OBSOLETE, TO BE REMOVED WHEN DESKTOP REMOVES CODE
     */
    public static Media = {
        /** WebRTC audio call */
        AUDIO: 1,
        /** WebRTC audio and video call */
        VIDEO: 2,
        /** Telephony call (from pbx) */
        PHONE: 4,
        /** WebRTC Screen sharing call */
        SHARING: 8
    };

    /**
     * @deprecated OBSOLETE, TO BE REMOVED WHEN DESKTOP REMOVES CODE
     */
    public get type() {
        if (this.callType === CallType.WEBRTC) {
            return CallRB.Type.WEBRTC;
        }

        return CallRB.Type.PHONE;
    }

    // -----    DEFINITION KEPT FOR DESKTOP APP COMPATIBILITY   -----

    // ----------------------------------------------------
    // UCaaS Properties Only
    // ----------------------------------------------------

    private conversationService: ConversationServiceRB = ConversationServiceRB.getInstance();
    // Services that will be dynamically injected
    private callService: CallServiceRB = CallServiceRB.getInstance();

    private callRecorderService?: any;

    private rxSubject: Subject<any> = new Subject<any>();

    private durationTimer: any = null;

    public id: string;
    public conversationId: string;
    public connectionId: string;

    public onHold: boolean = false;
    public putOnHold: boolean = false;

    public networkQualityIteration: number = 1;

    //external popup management
    public isInExternalWindow: boolean = false;
    public externalWindowRef: any;

    public customCommandsRequested: any;
    public thirdPartyInfos: string;

    // ----------------------------------------------------
    //              SDK + UCaaS Properties
    // Private properties with specific Getter / Setter
    // ----------------------------------------------------

    private _contact?: Contact = undefined; //RQRAINB-6015

    private _status: CallStatus;
    private _muted: boolean = false;

    private _capabilities: CallCapabilities = {
        answer: false,
        release: false,
        deflectToVoicemail: false,
        addAudio: false,
        mute: false,
        unmute: false,
        addVideo: false,
        removeVideo: false,
        addSharing: false,
        removeSharing: false,
        blindTransfer: false,
        mergeCalls: false,
        addParticipantsToCall: false,
        retrieve: false,
        hold: false,
        dtmf: false,
        transferToOtherCall: false,
        transferCallDestinations: [],
        deflectToPhoneNumber: false,
        deflectPhoneNumberDestinations: [],
        startRecording: false,
        pauseRecording: false,
        resumeRecording: false,
        stopRecording: false,
        cancelRecording: false
    };

    // ----------------------------------------------------
    //              SDK + UCaaS Properties
    // ----------------------------------------------------

    /**
     * The type of the call (WebRTC or Telephony)
     */
    public callType: CallType;

    /**
     * Call duration in seconds
     */
    public duration: number = 0;

    /**
     * Call direction
     * This information is relevant only for telephone (PBX) call. For WebRTC call it is undefined.
     */
    public direction: CallDirection = CallDirection.UNDEFINED;

    /**
     * Media stream associated to the local party.
     * Typed as number BUT MUST follow the values defined by CallMedia type or a combination thereof.
     */
    public localMedia: number = CallMedia.NONE;

    /**
     * Media stream associated to the remote party.
     * Typed as number BUT MUST follow the values defined by CallMedia type or a combination thereof.
     */
    public remoteMedia: number = CallMedia.NONE;

    /**
     * NetworkQuality Value (0 - bad, 1 - medium, 2 - good) representing the current network quality for the call
     * @readonly
     */
    public networkQuality: number = 0;

    /**
     * On an incoming call, contains the subject of the call if this has been specified by the caller (inside purpose)
     * @readonly
     */
    public subject: string = undefined;

    public get localMedias(): CallMedias {
        return {
            audio: Boolean(this.localMedia & CallMedia.AUDIO),
            video: Boolean(this.localMedia & CallMedia.VIDEO),
            sharing: Boolean(this.localMedia & CallMedia.SHARING),
        };
    }

    public get remoteMedias(): CallMedias {
        return {
            audio: Boolean(this.remoteMedia & CallMedia.AUDIO),
            video: Boolean(this.remoteMedia & CallMedia.VIDEO),
            sharing: Boolean(this.remoteMedia & CallMedia.SHARING),
        };
    }

    public recorder: {
        state: CallRecordingState;
        getDuration: () => number;
    } = {
        state: CallRecordingState.RECORDING_STOPPED,
        getDuration: () => { return this.getRecordingDuration(); }
    };

    // ----------------------------------------------------
    //              !! TODO FOR SDK !!
    //  Check If some of these must be proposed to SDK
    //      UCaaS Data Only ... Until Now !
    // ----------------------------------------------------

    // Seems to be unused Properties ...
    public isEscalated: boolean = false;
    public isError: boolean = false;
    public unifiedPlanActivated: boolean = true;

    // Properties useful for SDKV2 ?
    public startDate = new Date();

    // Properties NOT useful for SDKV2 ?
    public isInitiator = false;
    public correlatorData: string = null;
    public correlatorDataHex: string = null;
    public isHidden: boolean = false;
    public makecallCtiExtern: boolean = false;

    // -----------------------------------------------------
    public isVm: boolean = false;

    public participants = null;
    public isConference = false;
    public avatars: any[];
    public fullJid: string = "";
    public mediaPillarCall: any = null;
    public isSecondary: boolean = false;
    public isSecondSharingCall: boolean = false;
    public relevant: boolean = true;
    public relevantEquipmentId: string = "unknown";
    public deviceId: any = null; //sipWise+
    public isOwner: any = undefined;
    public isMPAudioOwner: any = undefined;
    public pbxConnectionDown: any = undefined;
    public statusInfo: any;
    public globalCallId: string = null;

    public activeControl: boolean = false;
    public hasRemoteControlling: boolean = false;
    public sipWiseData: any = null;
    public sipWiseEndPointDeviceList: any = null;//RQRAINB-5427

    public noIncomingPopup: boolean = false;

    public errorMessage: string = null;

    public autoOffHookResource: string = undefined;
    public autoOffHookInProgress: boolean = undefined;
    public mixtWebTelSingleStepTrans: boolean = false;

    public localStreams: MediaStream[] = [];
    public pinnedMedia: string = "";
    public dimLocalSharingScreen: boolean = true;
    public statsInterval: any = null;
    public originalStream: MediaStream = null;
    public autoClear: any = null;
    public autoAnswer: boolean = false;
    public distantType: string = "";
    public addVideoAfterUnhold: boolean = false;

    public callLayoutSizeValues: any = {};

    public _redirectionCause: CallRedirectionCause = CallRedirectionCause.UNDEFINED;

    public get redirectionCause(): CallRedirectionCause {
        return this._redirectionCause;
    }

    public set redirectionCause(redirectionCause: CallRedirectionCause) {
        if (redirectionCause !== this._redirectionCause) {
            this._redirectionCause = redirectionCause;
            this.sendEvent('redirectionCause');
        }
    }

    private _isRecorded: boolean = false;

    public get isRecorded(): boolean {
        return this._isRecorded;
    }

    public set isRecorded(value: boolean) {
        if (value !== this._isRecorded) {
            this._isRecorded = value;
            this.sendEvent(CallEvents.ON_CALL_REMOTE_RECORDING_UPDATE);
        }
    }

    public callerNumber: string = null;

    public calledParty: CalledParty = null;

    public get calledNumber(): string {
        return this.calledParty?.phoneNumber;
    }

    public get alertingNumber(): string {
        return this.direction === CallDirection.OUTGOING
            ? this.remoteNumber
            : undefined;
    }

    public get remoteNumber(): string {
        return this.remoteParty?.phoneNumber;
    }

    public remoteParty: { rawPhoneNumber: string; phoneNumber: string; contact: any; jid: string; };
    public remoteParties: { rawPhoneNumber: string; phoneNumber: string; contact: any; jid: string; direction?: CallDirection; }[];

    public isWebrtcMpIncomingCallPopup: boolean = false;

    public webConferenceRoom: any;

    public remoteControled: boolean = false; //CRRAINB-26056 for sipwise flag when call is managed from another client instance

    //in case of supervision Hybride, mark the call that is managed on another device; This is managed by supervision service, and will be used only
    // on the UI part to hide the tel-call-area for that call
    public remoteControledHybride: boolean = false;

    public transferInitiatorUserId: string;

    public role: string;

    public ccdGlobalWaitingTime: string;

    // ----------------------------------------------------
    //                  CALL MODEL METHODS
    // ----------------------------------------------------

    private _lastCause: string = null;
    public get lastCause(): string {
        return this._lastCause;
    }

    public set lastCause(cause: string) {
        if (cause !== this._lastCause) {
            this._lastCause = cause;
            this.sendEvent('lastCause');
        }
    }

    private _PBXDisplay: string = null;//RQRAINB-6015
    public get PBXDisplay(): string {
        return this._PBXDisplay;
    }

    public set PBXDisplay(display: string) {
        if (display !== this._PBXDisplay) {
            this._PBXDisplay = display;
            this.sendEvent('PBXDisplay');
        }
    }

    public get isDoorphoneCall(): boolean {
        return (this.mediaPillarCall?.webrtcCallRef?.remoteMedias?.video);
    }

    public hasRemoteSharingWithAudio: boolean = false;

    // ----------------------------------------------------
    //                      UCAAS ONLY
    // ----------------------------------------------------

    constructor(status: CallStatus, id: string, type: CallType, contact: Contact) {
        /**
         * @public
         * @name status
         * @enum {object} The status of the call (e.g. dialing, active, releasing...)
         * @property {object} UNKNOWN Call Cleared
         * @property {object} DIALING In dialing
         * @property {object} QUEUED_INCOMMING In queue (user side)
         * @property {object} QUEUED_OUTGOING In queue (recipient side)
         * @property {object} RINGING_INCOMMING Call in ringing (user side)
         * @property {object} RINGING_OUTGOING Call in ringing (recipient side)
         * @property {object} ACTIVE Active call
         * @property {object} HOLD Call on hold
         * @property {object} PUT_ON_HOLD Call put on hold
         * @property {object} RELEASING Call is clearing
         * @property {object} ANSWERING Call is being answered
         * @property {object} CONNECTING Trying to reconnect the call
         * @property {object} ERROR Call error
         * @readonly
         */
        this.setStatus(status);

        this.callType = type;

        /**
         * @public
         * @name id
         * @param {string}
         * @description The ID of the call
         * @readonly
         * @static
         */
        this.id = id;

        /**
         * @public
         * @name conversationId
         * @property {string} conversationId The reference of the conversation
         * @readonly
         */
        this.conversationId = null;

        /**
         * @private
         * @property {string} connectionId The CSTA connection (or leg) identifier
         * @readonly
         */
        this.connectionId = null;

        /**
         * @private
         * @property {boolean} isVm Indicates whether it is a call to user's own voicemail
         * @readonly
         */
        this.isVm = null;

        this.contact = contact;
        this.remoteMedia = 0;
        this.localMedia = 0;

        /**
         * @public
         * @name isEscalated
         * @property {boolean} isEscalated True if the call has been escalated to sharing or video (only relevant for WebRTC calls)
         * @readonly
         */
        this.isEscalated = false;

        /**
         * @public
         * @name startDate
         * @property {Date} startDate The date when the call has been started
         * @readonly
         */
        this.startDate = new Date();

        /**
         * @public
         * @name isInitiator
         * @property {boolean} isInitiator True if the user is the caller (false if he's the called)
         * @readonly
         */
        this.isInitiator = false;

        /**
         * @public
         * @name participants
         * @property {Contact[]} participants The list of participants in the call (conference)
         * @readonly
         */
        this.participants = null;

        /**
         * @public
         * @name isConference
         * @since 1.20
         * @property {boolean} isConference True if the call is a conferenceCall
         * @readonly
         */
        this.isConference = false;

        /**
         * @public
         * @name avatars
         * @property {string} avatars array of call avatar sources
         * @readonly
         */
        this.avatars = contact && contact.avatar ? [this.contact.avatar.src] : [];

        this.subject = undefined;

        /**
         * @public
         * @name remoteParty
         * @property {object}  remoteParty Contains remote party's phoneNumber and contact
         * @readonly
         */
        this.remoteParty = { rawPhoneNumber: "", phoneNumber: "", jid: "", contact: contact };

        /**
         * @public
         * @name remoteParties
         * @property {object}  remoteParties Contains remote parties number and contact (for 3 parties pbx conference)
         * @readonly
         */
        this.remoteParties = null;

        /**
         * @public
         * @name fullJid
         * @property {string}  fullJid The full jid of the user in the call
         * @readonly
         */
        this.fullJid = "";

        /**
         * @public
         * @name mediaPillarCall
         * @property {object} mediaPillarCall Specific VoIP (MediaPillar) call context
         * @readonly
         */
        this.mediaPillarCall = null;

        /**
         * @public
         * @name isSecondary
         * @property {object} isSecondary is nomadic secondary phone selected
         * @readonly
         */
        this.isSecondary = false;

        /**
         * @public
         * @name isSecondSharingCall
         * @property {boolean} isSecondSharingCall if the call is a sharing-only call on top of PBX (Media Pillar) call with the same contact
         * @readonly
         */
        this.isSecondSharingCall = false;

        /**
         * @private
         * @name relevant
         * @property {object} relevant is this call relevant for Rainbow (use in nomadic and MP multidevice cases)
         * @readonly
         */
        this.relevant = false;

        /**
         * @private
         * @name relevantEquipmentId
         * @property {string} relevantEquipmentId is this call relevant equipment identifier for rainbow (use in nomadic and MP multidevice cases)
         * @readonly
         */
        this.relevantEquipmentId = "unknown";

        /**
         * @private
         * @name deviceId
         * @property {object}  deviceId used Only for sipWise case
         * @readonly
         */
        this.deviceId = null;

        /**
         * @public
         * @name isOwner
         * @property {object} isOwner (PHONE CALLS ONLY): is this call owned (initiated) by my device (used in calls made by makecall; may also be used later for incoming calls
         *  taken by webclient)
         * NOTE: isOwner is also TRUE if call is accepted by the client, but this works only for OFFICE MODE (it might not always work for MP call in case of call id change for Hybride)
         */
        this.isOwner = undefined;

        /**
         * @public
         * @name isMPAudioOwner
         * @property {object} isMPAudioOwner is this call audio owned by my device: it may be true even if isOwner is false (external cti makecall usecase)
         */
        this.isMPAudioOwner = undefined;

        /**
         * @private
         * @name pbxConnectionDown
         * @property {object} pbxConnectionDown indicates wether the telephony connection to server/pbx is down : used to handle mediapillar calls
         * during a pbx agent shutdown
         */
        this.pbxConnectionDown = undefined;

        this.isMuted = false;
        this.duration = 0;

        /**
         * @private
         * @property {object} statusInfo
         * @readonly
         */
        this.statusInfo = null;

        /**
         * @public
         * @property {string} globalCallId a global id when the call go thrue PBX Node.
         * @readonly
         */
        this.globalCallId = undefined;

        /**
         * @public
         * @property {string} correlatorData Data about a call
         * @readonly
         */
        this.correlatorData = undefined;

        /**
         * @public
         * @property {string} correlatorData Data about a call - in hex format
         * @readonly
         */
        this.correlatorDataHex = undefined;

        /**
         * @public
         * @name activeControl
         * @since 2.0.0
         * @property {boolean} activeControl True if active remote control is activated
         * @readonly
         */
        this.activeControl = false;

        /**
         * @public
         * @name hasRemoteControlling
         * @since 2.0.0
         * @property {boolean} hasRemoteControlling True if remote user is controlling this PC
         * @readonly
         */
        this.hasRemoteControlling = false;

        this.networkQuality = 0;

        /**
         * @private
         * @name sipWiseData
         * @since 2.0.0
         * @property {object} sipWiseData
         * @readonly
         */
        this.sipWiseData = { initCause: "", called: { phoneNumber: "", displayName: "" }, assocPhoneCallId: null };

        /**
         * @private
         * @name sipWiseEndPointDeviceList
         * @since 2.0.0
         * @property {object} sipWiseEndPointDeviceList
         * @readonly
         */
        this.sipWiseEndPointDeviceList = [];

        /**
         * @public
         * @name PBXDisplay
         * @property {string} PBXDisplay PBX message display for PILOT informations
         * @readonly
         */
        this.PBXDisplay = undefined;

        /**
         * @public
         * @name role
         * @property {string} role role of the device
         * @readonly
         */
        this.role = undefined;

        /**
         * @public
         * @name noIncomingPopup
         * @property {boolean} noIncomingPopup True if no incomingpopup must be displayed
         * @readonly
         */
        this.noIncomingPopup = false;

        /**
         * @public
         * @name onHold
         * @property {boolean} onHold True if the call is on hold from the local party (only for P2P calls !)
         * @readonly
         */
        this.onHold = false;

        /**
         * @public
         * @name putOnHold
         * @property {boolean} putOnHold True if call is put on hold from the distant party (only for P2P calls !)
         * @readonly
         */
        this.putOnHold = false;

        /**
         * @public
         * @name localStreams
         * @property {MediaStream[]} localStreams Array containing all local streams related to this call (it's either 1 or 2 max)
         * @readonly
         */
        this.localStreams = [];

        this.direction = CallDirection.UNDEFINED;

        /**
         * @public
         * @name callerNumber
         * @property {string} callerNumber Pbx call's caller number
         * @readonly
         */
        this.callerNumber = undefined;

        /**
         * @public
         * @name calledParty
         * @property {any} calledParty Pbx call's initial called party (only set if call has been redirected)
         * @readonly
         */
        this.calledParty = undefined;

        /**
         * @private
         * @name pinnedMedia
         * @property {string} pinnedMedia The pinned distant media (UI UCAAS Property) // could be "video" or "sharing" or null
         * @readonly
         */
        this.pinnedMedia = "";

        /**
         * @private
         * @name dimLocalSharingScreen
         * @property {boolean} dimLocalSharingScreen Whether the local sharing is dimmed or not
         * @readonly
         */
        this.dimLocalSharingScreen = true;

        /**
         * @private
         * @name statsInterval
         * @property {any} statsInterval The Window.interval reference to the session statistics interval
         * @readonly
         */
        this.statsInterval = null;

        /**
         * @private
         * @name originalStream
         * @property {MediaStream} originalStream The original video stream (in case we use background replacement)
         * @readonly
         */
        this.originalStream = null;

        /**
         * @private
         * @name autoClear
         * @property {any} autoClear For TO keep alive management
         * @readonly
         */
        this.autoClear = null;

        /**
         * @private
         * @name autoAnswer
         * @property {any} autoAnswer In case of make call CTI, the popup is not displyed, and the answer is automatically done
         * @readonly
         */
        this.autoAnswer = false;

        /**
         * @private
         * @name distantType
         * @property {string} distantType In case of RB Hub, we know the distant type of configuration used (could be computer or office);
         * If the value is "webrtc" this means that the distant is in computer mode too
         * @readonly
         */
        this.distantType = "";

        /**
         * @private
         * @name addVideoAfterUnhold
         * @property {boolean} addVideoAfterUnhold When we put a call on hold, we should remember if we need to re-add the video after retrieve;
         * @readonly
         */
        this.addVideoAfterUnhold = false;
        /**
         * @private
         * @name mixtWebTelSingleStepTrans
         * @property {boolean} mixtWebTelSingleStepTrans
         * to flag a special case of sipwise singleStepTransfer with both webrtc and sip
         */
        this.mixtWebTelSingleStepTrans = false;

        /**
         * @private
         * @name webConferenceRoom
         * @property {boolean} webConferenceRoom
         * Web Conference Room to which the call is linked (used in usecase "join by phone to a web conference call")
         */
        this.webConferenceRoom = null;

        /**
         * @private
         * @name onRemoteCtrlCall
         * @property {boolean} onRemoteCtrlCall
         * setted when a call is managed from another same client for instance on rainbow mobile app (used only in sipwise situation)
         */
        this.remoteControled = false; //CRRAINB-26056

        /**
         * @private
         * @name onRemoteCtrlCall
         * @property {boolean} remoteControledHybride
         * setted when a call is managed on another device (HYBRID ONLY) and managed by the SUPERVISION SERVICE !
         */
        this.remoteControledHybride = false;

        /**
         * third party informations for the call that are received from the desktop app (from external source)
        */
        this.thirdPartyInfos = '';

        this.callRecorderService = MainService.getInstance().inject(CALL_RECORDER_SVC);

        /**
         * @public
         * @property {string} ccdGlobalWaitingTime the waiting time of the ccd call
         * @readonly
         */
        this.ccdGlobalWaitingTime = undefined;
    }

    public static create(status: any, id: string, type: CallType, contact?: any) {
        return new CallRB(status, id, type, contact);
    }

    // ----------------------------------------------------
    // Getter/Setter for Call properties
    // ----------------------------------------------------

    // The user reference representing the other party (as caller or callee) engaged in this call
    public get contact(): Contact {
        return this._contact;
    }

    public set contact(contact: Contact) {
        this._contact = contact;
        this.sendEvent('contact');
    }

    // Call capabilities
    public get capabilities(): CallCapabilities {
        return this._capabilities;
    }

    /**
     * Update globally the capabilities of the call.
     * Tests are performed to check whether capabilities have been modified and whether the data needs to be updated.
     * This avoids sending an 'ON_CALL_CAPABILITIES_UPDATED' event if no changement has been detected.
     * @param capabilities - The new values of the capabilities
     */
    public set capabilities(capabilities: CallCapabilities) {
        //compare both capabilities ...
        let update = false;
        Object.keys(this._capabilities).forEach((key) => {
            switch (key) {
                case 'transferCallDestinations': {
                    let dataUpdated = (this._capabilities['transferCallDestinations'].length !== capabilities['transferCallDestinations'].length);
                    if (!dataUpdated && this._capabilities['transferCallDestinations'].length) {
                        //compare array items
                        dataUpdated = !this._capabilities['transferCallDestinations'].every((call: CallRB) => {
                            return capabilities['transferCallDestinations'].find(call2 => ((call2 as CallRB).id) === call.id);
                        });
                    }
                    update = update || dataUpdated;
                    break;
                }

                case 'deflectPhoneNumberDestinations': {
                    // deflectPhoneNumberDestinations: [{ id: string; number: string }?];
                    let dataUpdated = (this._capabilities['deflectPhoneNumberDestinations'].length !== capabilities['deflectPhoneNumberDestinations'].length);
                    if (!dataUpdated) {
                        //compare array items
                        dataUpdated = !this._capabilities['deflectPhoneNumberDestinations'].every(elem1 =>
                            capabilities['deflectPhoneNumberDestinations'].some(
                                elem2 => elem1.id === elem2.id && elem1.number === elem2.number
                            )
                        );
                    }
                    update = update || dataUpdated;
                    break;
                }

                default:
                    if (this._capabilities[key] !== capabilities[key]) {
                        update = true;
                    }
                    break;
            }
        });
        if (update) {
            this._capabilities = capabilities;
            this.sendEvent(CallEvents.ON_CALL_CAPABILITIES_UPDATED);
        }
    }

    // Call local Mute state
    public get isMuted(): boolean {
        return this._muted;
    }

    public set isMuted(muted: boolean) {
        if (muted !== this._muted) {
            this._muted = muted;
            this.sendEvent(CallEvents.ON_CALL_MUTE_CHANGE);
        }
    }

    // Call Status
    public get callStatus(): CallStatus {
        return this._status;
    }

    public set callStatus(status: CallStatus) {
        if (this._status !== status) {
            this._status = status;
            this.sendEvent(CallEvents.ON_CALL_STATUS_CHANGE);
            this.conversationService.onCallStatusChange(this);
        }
    }

    public setCallId(id: string) {
        this.id = id;
    }

    public getGlobalCallId() {
        return this.globalCallId;
    }

    public setGlobalCallId(id: string) {
        this.globalCallId = id;
    }

    public setConversationId(conversationId: string) {
        this.conversationId = conversationId;
    }

    public setStatus(status: CallStatus) {
        this.callStatus = status;

        //keep compatibility for desktop;
        this.status = CallRB.Status[status];
        this.manageIncomingPopupData();
        this.manageDurationTimer();
    }

    public setCorrelatorData(correlatorData: string) {
        this.correlatorData = correlatorData;
    }

    public setCorrelatorDataHex(correlatorDataHex: string) {
        this.correlatorDataHex = correlatorDataHex;
    }

    public setIsVm(isVM: boolean) {
        this.isVm = isVM;
    }

    public getIsVm(): boolean {
        return this.isVm;
    }

    public setContact(contact: any) {
        this.contact = contact;
        this.avatars = [this.contact.avatar.src];
    }

    public setSubject(subject: string): void {
        this.subject = subject;
    }

    public getRemoteParty() {
        return this.remoteParty;
    }

    public setRemoteParty(rawPhoneNumber: string, jid: string, contact: any, phoneNumber?: string) {
        if (!this.remoteParty) { this.remoteParty = { rawPhoneNumber: "", jid: "", phoneNumber: "", contact: null }; }
        this.remoteParty.rawPhoneNumber = rawPhoneNumber;
        this.remoteParty.phoneNumber = phoneNumber || rawPhoneNumber;
        this.remoteParty.contact = contact;
        this.remoteParty.jid = jid || "";
        this.remoteParties = null;
    }

    public getRemoteParties() {
        return this.remoteParties;
    }

    public setRemoteParties(remoteParties: any[]) {
        this.remoteParty = null;
        this.remoteParties = remoteParties;
    }

    public getRemotePartyPhoneNumber() {
        return this.remoteParty ? this.remoteParty.phoneNumber : "";
    }

    public getRemotePartyRawPhoneNumber() {
        return this.remoteParty ? this.remoteParty.rawPhoneNumber : "";
    }

    public setRemotePartyPhoneNumberAndJid(rawPhoneNumber: string, jid: string, phoneNumber?: string) {
        if (!this.remoteParty) { this.remoteParty = { rawPhoneNumber: "", jid: "", phoneNumber: "", contact: null }; }
        this.remoteParty.rawPhoneNumber = rawPhoneNumber;
        this.remoteParty.phoneNumber = phoneNumber || rawPhoneNumber;
        this.remoteParty.jid = jid || "";
        this.remoteParties = null;
    }

    public setRemotePartyContact(contact: any, jid: string) {
        if (!this.remoteParty) { this.remoteParty = { rawPhoneNumber: "", jid: "", phoneNumber: "", contact: null }; }
        this.remoteParty.contact = contact;
        this.remoteParty.jid = jid || "";
        this.remoteParties = null;
    }

    public setRelevantEquipmentId(relevantEquipmentId: string) {
        this.relevantEquipmentId = relevantEquipmentId;
    }

    public setParticipants(participants: any[]) {
        this.participants = participants;
        this.avatars = [];
        this.participants.forEach((participant) => {
            this.avatars.push(participant.avatar.src);
        });
    }

    public setConnectionId(connectionId: string) {
        this.connectionId = connectionId;
        this.id = CallRB.getIdFromConnectionId(connectionId);
    }

    public getccdGlobalWaitingTime() {
        return this.ccdGlobalWaitingTime;
    }

    public setccdGlobalWaitingTime(waitingTime: string) {
        this.ccdGlobalWaitingTime = waitingTime;
    }
    // ----------------------------------------------------
    //  TOOLS METHODS
    // ----------------------------------------------------

    /**
     * @public
     * @param name - the name of the event
     * @param data - the data to be send (an object)
     * CallEvents events send to other services via sendEvent()
     * Can listen to it via Call.subscribe() API
     */
    public sendEvent(name: string, data: any = null) {
        this.rxSubject.next(RBEvent.create(name, data));
    }

    public isInConversationWithMobile() {
        if (!this.fullJid) {
            return false;
        }
        return this.fullJid.indexOf("mobile_android") !== -1 || this.fullJid.indexOf("mobile_ios") !== -1;
    }

    public isInConversationWithDesktopApp() {
        if (!this.fullJid) {
            return false;
        }
        return this.fullJid.indexOf("desk_") !== -1;
    }

    public toString() {
        let typeInfo: string = "Undefined";

        switch (this.callType) {
            case CallType.WEBRTC:
                typeInfo = "Video";
                break;

            case CallType.PHONE:
                typeInfo = "Phone";
                break;
            default: break;
        }

        let myString = `(type: ${typeInfo}, id: ${this.id}, status: ${this.callStatus}`;
        if (this.isConference) { myString += " conference"; }
        myString += ")";
        return myString;
    }

    public toJSON(): any {

        let typeInfo: string = "Undefined";

        switch (this.callType) {
            case CallType.WEBRTC:
                typeInfo = "Video";
                break;

            case CallType.PHONE:
                typeInfo = "Phone";
                break;
            default: break;
        }

        const json: any = {
            id: this.id,
            type: typeInfo,
            status: this.callStatus
        };

        if (this.contact) {
            json.contact = {
                id: this.contact.dbId,
            };
            if (this.contact?.name) {
                json.contact.name = this.contact.name;
            }
        }

        this.toString();

        return json;
    }

    // ----------------------------------------------------
    //  DURATION MANAGEMENT
    // ----------------------------------------------------

    private manageDurationTimer() {
        if (this.callStatus === CallStatus.ACTIVE) {
            //in case of ACTIVE call, start the TIMER
            this.startDuration();
        }
        else if (this.callStatus === CallStatus.ENDED) {
            //in case of UNKNOWN state, or in case of transfer
            this.stopDuration();
        }
    }

    private manageIncomingPopupData() {
        if (this.callStatus === CallStatus.ENDED) {
            //in case of UNKNOWN state, clear noIncomingPopup property
            this.noIncomingPopup = false;
        }
    }

    public startDuration() {
        if (this.durationTimer) return;
        this.durationTimer = interval(1000).subscribe(() => {
            this.duration++;
        });
    }

    public stopDuration() {
        if (this.durationTimer) {
            this.durationTimer.unsubscribe();
            this.durationTimer = null;
            this.duration = 0;
        }
    }

    // ----------------------------------------------------
    //  TELEPHONY STUFF
    // ----------------------------------------------------

    public static getIdFromConnectionId(connectionId: string) {
        return connectionId;
    }

    public static getCallIdFromConnectionId(connectionId: string) {
        return connectionId ? connectionId.split("#")[0] : null;
    }

    public static getDeviceIdFromConnectionId(connectionId: string) {
        const deviceId = connectionId ? connectionId.split("#")[1] : null;
        return deviceId ? deviceId : null;
    }

    public getDeviceIdFromConnectionIdFromCall() {
        //for the moment only for sipWise case
        if (this.deviceId) {
            return this.deviceId;
        }
        //else former way
        return this.connectionId ? this.connectionId.split("#")[1] : null; //not sip case
    }

    // ----------------------------------------------------
    //  MEDIA PILLAR STUFF
    // ----------------------------------------------------

    public isWebRtcMediaPillar() {
        return (this.fullJid && this.fullJid.indexOf("mp_") === 0);
    }

    /**
     * Check if the call is linked to a media pillar context
     * Note: if it is a new call not already managed through webrtcGatewayService then not conclusive
     * (because could be linked after)
     */
    public isMediaPillarCall() {
        return this.mediaPillarCall !== null || this.isWebRtcMediaPillar();
    }

    public isInCallWithMediaPillar() {
        return this.fullJid && this.fullJid.indexOf("mp_") !== -1;
    }

    public getMediaPillarCall() {
        return this.mediaPillarCall;
    }

    public setMediaPillarCall(mediaPillarCallContext: any) {
        this.mediaPillarCall = mediaPillarCallContext ? mediaPillarCallContext : null;
    }

    // ----------------------------------------------------
    //  TELEPHONY SIPWISE STUFF
    // ----------------------------------------------------

    public setDeviceIdSipWise(deviceId: string) {
        this.deviceId = deviceId ? deviceId : null;
    }

    public getDeviceIdSipWise() {
        return this.deviceId;
    }

    public setConnectionIdSipWise(connectionId: string) {
        //this.id not equal to this.connectionId
        this.connectionId = connectionId;
    }

    public getConnectionIdSipWise() {
        return this.connectionId;
    }

    public resetSipWiseData() {
        this.sipWiseData = { initCause: "", called: { phoneNumber: "", displayName: "" } };
    }

    public setInitCauseSipWiseData(cause: string) {
        this.sipWiseData.initCause = cause;
        this.setLastCause(cause);
    }

    public getLastCause(): string {
        return this.lastCause;
    }

    public setLastCause(cause: string): void {
        this.lastCause = cause;
        if (cause === "callForwardImmediate" || cause === "callForwardNoAnswer" || cause === "callForwardBusy" || cause === "redirected") {
            this.redirectionCause = CallRedirectionCause.FORWARDED;
        } else if (cause === "distributed") {
            this.redirectionCause = CallRedirectionCause.HUNTGROUP;
        }
    }

    public getInitCauseSipWiseData() {
        return this.sipWiseData.initCause;
    }

    public setTypeSipWiseData(type: string) {
        this.sipWiseData.type = type;
    }

    public getTypeSipWiseData() {
        return this.sipWiseData.type;
    }

    /**
     * For a sipwise (cloud tel) incoming call to an user who is belong to a hunting group
     * We have to known if the call is for the group or privatly for the user
     * This methode gives this info.
     * @returns Return true if the call is for the hunting group false otherwise
     */
    public isHuntingGroupCallSipWise(): boolean {
        return this.sipWiseData.initCause === "distributed";
    }

    public isHuntingGroupCallQueueSipWise(): boolean {
        return this.sipWiseData.type === "ccq";
    }

    //call is coming from the SFU, as part of add/call participant from bubble
    public isSipWiseSfuIncomingCall(): boolean {
        return this.sipWiseData?.type === "sfu";
    }

    public isSfuPbxCall(): boolean {
        return this.isSipWiseSfuIncomingCall() || Boolean(this.webConferenceRoom);
    }

    public setAssocPhoneCallId(assocPhoneCallId) {
        this.sipWiseData.assocPhoneCallId = assocPhoneCallId;
    }

    public getAssocPhoneCallId() {
        return this.sipWiseData.assocPhoneCallId;
    }

    public setEndPointDeviceListSipWise(deviceList: any) { //RQRAINB-5427
        //associate to call the endPoint deviceList attributs (obtained from handler)
        this.sipWiseEndPointDeviceList = deviceList;
    }

    public getEndPointDeviceListSipWise() { //RQRAINB-5427
        //get endPoint deviceList attributs (obtained from handler)
        return this.sipWiseEndPointDeviceList;
    }

    public setAutoOffHookResource(resource: string): void {
        this.autoOffHookResource = resource;
    }

    public getAutoOffHookResource(): string {
        return this.autoOffHookResource;
    }

    public setAutoOffHookInProgress(inProgress: boolean): void {
        this.autoOffHookInProgress = inProgress;
    }

    public getAutoOffHookInProgress(): boolean {
        return this.autoOffHookInProgress;
    }

    public setMixtWebTelSingleStepTrans(flag: boolean) {
        this.mixtWebTelSingleStepTrans = flag;
    }

    public getMixtWebTelSingleStepTrans() {
        return this.mixtWebTelSingleStepTrans;
    }

    public setDirection(direction: CallDirection): void {
        this.direction = direction;
        this.isInitiator = (this.direction === CallDirection.OUTGOING);
    }

    public getDirection(): CallDirection {
        return this.direction;
    }

    public getRecordingDuration(): number {
        return this.callRecorderService ? this.callRecorderService?.getRecordingDuration(this) : 0;
    }

    // ----------------------------------------------------
    //  UI management
    // ----------------------------------------------------

    // UCaaS Only: UI management of telephone status
    public static StatusDisplay = {
        UNKNOWN: "",
        ENDED: "",
        DIALING: "dialing",
        QUEUED_INCOMMING: "incoming",
        QUEUED_OUTGOING: "ringingOutgoing",
        RINGING_INCOMMING: "incoming",
        RINGING_OUTGOING: "ringingOutgoing",
        ACTIVE: "active",
        HOLD: "held",
        PUT_ON_HOLD: "putOnHold",
        RELEASING: "releasing",
        ANSWERING: "answering",
        CONNECTING: "connecting",
        ERROR: "failed"
    };

    public getStatusForDisplay(): string {
        return (this.callStatus) ? CallRB.StatusDisplay[this.callStatus] : "";
    }

    /**
     * Attach the local video stream to a given HTML VIDEO tag element;
     * @param HtmlElement - TheHTML VIDEO tag element visible on the page
     */
    public attachLocalVideoStreamToElement(HtmlElement: HTMLElement): void {
        if (!this.localMedias?.video) throw new Error("[CALL] attachLocalVideoStream -- no local video available");
        this.callService.attachStreamToExternalHtmlElement(this, HtmlElement, CallMedia.VIDEO, true);
    }

    /**
     * Attach the local sharing stream to a given HTML VIDEO tag element;
     * @param HtmlElementId - The HTML VIDEO tag element visible on the page
     */
    public attachLocalSharingStreamToElement(HtmlElement: HTMLElement): void {
        if (!this.localMedias?.sharing) throw new Error("[CALL] attachLocalSharingStream -- no local sharing available");
        this.callService.attachStreamToExternalHtmlElement(this, HtmlElement, CallMedia.SHARING, true);
    }

    /**
     * Attach the remote video stream to a given HTML VIDEO tag element;
     * @param HtmlElementId - The HTML VIDEO tag element visible on the page
     */
    public attachRemoteVideoStreamToElement(HtmlElement: HTMLElement): void {
        if (!this.remoteMedias?.video) throw new Error("[CALL] attachRemoteVideoStream -- no remote video available");
        this.callService.attachStreamToExternalHtmlElement(this, HtmlElement, CallMedia.VIDEO, false);
    }

    /**
     * Attach the remote sharing stream to a given HTML VIDEO tag element;
     * @param HtmlElementId - The HTML VIDEO tag element visible on the page
     */
    public attachRemoteSharingStreamToElement(HtmlElement: HTMLElement): void {
        if (!this.remoteMedias?.sharing) throw new Error("[CALL] attachRemoteSharingStream -- no remote sharing available");
        this.callService.attachStreamToExternalHtmlElement(this, HtmlElement, CallMedia.SHARING, false);
    }

    // ----------------------------------------------------
    //                SDK + UCAAS PUBLIC API
    // ----------------------------------------------------

    /**
     * Subscribe to updates on the current call (all events are of RBEvent<CallEvents>
     * @param callback - The callback function that will be called when events are received
     * @param eventNames - (optional) array of event to listen
     * @returns Returns RxJS Subscription
     */
    public subscribe(callback: (event: RBEvent<CallEvents>) => any, eventNames?: CallEvents | CallEvents[]): Subscription {
        const observable = eventNames
            ? this.rxSubject.pipe(filter((event: RBEvent<CallEvents>) => {
                if (eventNames instanceof Array) return eventNames.indexOf(event.name) !== -1;
                return event.name === eventNames;
            }))
            : this.rxSubject;

        return observable.subscribe((event: RBEvent<CallEvents>) => {
            try { callback(event); }
            catch (error) { }
        });
    }

    /**
     * Answer the current incoming call.
     * This feature can be used only if the "answer" call capability is 'True'.
     * By default, the answer is done with AUDIO only.
     * If the 'withVideo' option is set to 'True' and the incoming call contains video, the answer will directly be done with audio and video.
     * @param withVideo - Option to answer the call directly with audio and video. Default value is 'False'
     */
    public async answer(withVideo?: boolean): Promise<void> {
        if (!withVideo) { withVideo = false; }
        return this.callService.answerCall(this, withVideo);
    }

    /**
     * Release the current call.
     * This method allows to decline an incoming call or stop an outgoing call.
     * This feature can be used only if the "release" call capability is 'True'.
     */
    public async release(): Promise<void> {
        return this.callService.releaseCall(this);
    }

    /**
     * Put on hold the current call.
     * This feature can be used only if the "hold" call capability is 'True'.
     */
    public async hold(): Promise<void> {
        return this.callService.holdCall(this);
    }

    /**
     * Retrieve from hold the current call.
     * This feature can be used only if the "retrieve" call capability is 'True'.
     */
    public async retrieve(): Promise<void> {
        return this.callService.retrieveCall(this);
    }

    /**
     * Mute the call
     * This feature can be used only if the "mute" call capability is 'True'.
     */
    public mute(): void {
        this.callService.muteCall(this, true);
    }

    /**
     * Unmute the call
     * This feature can be used only if the "unmute" call capability is 'True'.
     */
    public unmute(): void {
        this.callService.muteCall(this, false);
    }

    // PMA: CHEKS MUST BE DONE (RRO, DOBRO ?)
    // Use of call.sendDTMF(...) instead of use of 'sendDTMF' in the dedicated service
    //  - webrtcP2PService.sendDTMF(call, ...);
    //  - telephonyService.sendDtmf(call, ...);
    // Warning: criteria used for approriate DTMF service could be different from call.service or may be too specific to make optimization
    // Check to do in: softphoneHeaderArea.component.ts + telCallArea.component.ts
    // PMA: CHEKS MUST BE DONE (RRO, DOBRO ?)
    /**
     * Send DTMF signal to the current call.
     * This feature can be used only if the "dtmf" call capability is 'True'.
     * @param dialKey - the DTMF character sequences to be send
     * Only the following characters are authorised: the ten single digits (0..9), the letters (a,b,c,d), the asterisk (*) and the pound sign (#).
     */
    public sendDTMF(dialKey: string): void {
        this.callService.sendDTMF(this, dialKey);
    }

    /**
     * Add the AUDIO media to the current call.
     * This feature can be used only if the "addAudio" call capability is 'True' and available only for WebRTC Calls.
     */
    public async addAudio(): Promise<void> {
        return this.callService.addMediaToCall(this, MediaType.AUDIO);
    }

    /**
     * Add the VIDEO media to the current call.
     * This feature can be used only if the "addVideo" call capability is 'True' and available only for WebRTC Calls.
     */
    public async addVideo(): Promise<void> {
        return this.callService.addMediaToCall(this, MediaType.VIDEO);
    }

    /**
     * Add the SHARING media to the current call.
     * This feature can be used only if the "addSharing" call capability is 'True' and available only for WebRTC Calls.
     */
    public async addSharing(): Promise<void> {
        return this.callService.addMediaToCall(this, MediaType.SHARING);
    }

    /**
     * Remove the VIDEO media from the current call.
     * This feature can be used only if the "removeVideo" call capability is 'True' and available only for WebRTC Calls.
     */
    public async removeVideo(): Promise<void> {
        return this.callService.removeMediaFromCall(this, MediaType.VIDEO);
    }

    /**
     * Remove the SHARING media from the current call.
     * This feature can be used only if the "removeSharing" call capability is 'True' and available only for WebRTC Calls.
     */
    public async removeSharing(): Promise<void> {
        return this.callService.removeMediaFromCall(this, MediaType.SHARING);
    }

    /**
     * Add participants to the current call.
     * The participants could be other Rainbow users or phone numbers (if the PBX system supports it).
     * This will result in a new conversation being created with web conference inside. The active call will be "transferred" to the bubble conference.
     * @param participants - The list of participants to be added to the call
     * @returns The new bubble conversation where the web conference is active
     */
    public async addParticipantsToCall(participants: CallDestination[]): Promise<Conversation> {
        return this.callService.addParticipantsToCall(this, participants);
    }

    /**
     * Create a conference from 2 calls, including the current call.
     * This will lead to the creation of either a web conference (managed via a Rainbow bubble) or a pure telephone conference (managed by a PBX system).
     * In the first case, a new conversation is created with the web conference inside. The two calls will be "transferred" to the bubble conference.
     * In the second case, a new 'conference' type conversation is created. This applies only with a Rainbow Hybrid (OXO or OXE) telephone system.
     * This feature can be used only if the "mergeCalls" call capability is 'True'.
     * @returns The new conversation or undefined depending on the type of conference created.
     */
    public async activateConference(): Promise<Conversation | undefined> {
        return this.callService.mergeCalls();
    }

    /**
     * Blind transfer the current call another destination which could be any rainbow user or phone number.
     * A blind transfer is when you transfer a call directly to another party without consulting with the person receiving the call.
     * In other words, there is no need to call the other party to make the transfer.
     * This feature is available only if the connected user is behind a Rainbow Hub telephone system.
     * @param destination - The destination of the blind transfer
     */
    public async blindTransfer(destination: CallDestination): Promise<void> {
        return this.callService.blindTransfer(this, destination);
    }

    /**
     * Deflect the incoming call to the voice mail.
     * This feature can be used only if the "deflectToVoicemail" call capability is 'True'.
     * The 'deflect to voice mail' is only available on HYBRID telephony systems.
     * For Cloud telephony systems, the release call will follow the user configuration (which could be deflect or other).
     */
    public async deflectToVoicemail(): Promise<void> {
        return this.callService.deflectToVoicemail(this);
    }

    /**
     * @public
     * Deflect (redirect) the current incoming call to the specified phone number.
     * This feature can be used only if the "deflectToPhoneNumber" call capability is 'True'.
     * The list of number where we can deflect the incoming call is available in the 'deflectPhoneNumberDestinations' call capability.
     * @param number - The phone number the call will be deflected to.
     */
    public async deflectToPhoneNumber(number: string): Promise<void> {
        return this.callService.deflectToPhoneNumber(this, number);
    }

    /**
     * Transfer the current call to another call.
     * If the destination call is not specified, then the transfer will be done towards the current active call.
     * The list of destination calls to which we can deflect the call current is available in the 'transferCallDestinations' call capability.
     * @param destinationCall - The destination call to which we want to transfer the current call.
     */
    public async transferToOtherCall(destinationCall?: Call): Promise<void> {
        return this.callService.transferToOtherCall(this, destinationCall as CallRB);
    }

    /**
     * Attach the local video stream to a given HTML VIDEO tag element;
     * @param HtmlElementId - The ID of the HTML VIDEO tag element visible on the page
     */
    public attachLocalVideoStream(HtmlElementId: string, HtmlElement?: HTMLElement): void {
        if (!this.localMedias?.video) throw new Error("[CALL] attachLocalVideoStream -- no local video available");
        this.callService.attachStreamToHtmlElement(this, HtmlElementId, CallMedia.VIDEO, true);
    }

    /**
     * Attach the local sharing stream to a given HTML VIDEO tag element;
     * @param HtmlElementId - The ID of the HTML VIDEO tag element visible on the page
     */
    public attachLocalSharingStream(HtmlElementId: string): void {
        if (!this.localMedias?.sharing) throw new Error("[CALL] attachLocalSharingStream -- no local sharing available");
        this.callService.attachStreamToHtmlElement(this, HtmlElementId, CallMedia.SHARING, true);
    }

    /**
     * Attach the remote video stream to a given HTML VIDEO tag element;
     * @param HtmlElementId - The ID of the HTML VIDEO tag element visible on the page
     */
    public attachRemoteVideoStream(HtmlElementId: string): void {
        if (!this.remoteMedias?.video) throw new Error("[CALL] attachRemoteVideoStream -- no remote video available");
        this.callService.attachStreamToHtmlElement(this, HtmlElementId, CallMedia.VIDEO, false);
    }

    /**
     * Attach the remote sharing stream to a given HTML VIDEO tag element;
     * @param HtmlElementId - The ID of the HTML VIDEO tag element visible on the page
     */
    public attachRemoteSharingStream(HtmlElementId: string): void {
        if (!this.remoteMedias?.sharing) throw new Error("[CALL] attachRemoteSharingStream -- no remote sharing available");
        this.callService.attachStreamToHtmlElement(this, HtmlElementId, CallMedia.SHARING, false);
    }

    /**
    * Start the recording of a call
    * @returns the call updated or an error
    */
    public async startRecording(): Promise<void> {
        if (!this.callRecorderService) {
            throw new Error("callRecorderService not injected");
        }
        return this.callRecorderService?.startRecording(this);
    }

    /**
    * Pause the recording
    * @returns the call updated or an error
    */
    public async pauseRecording(): Promise<void> {
        if (!this.callRecorderService) {
            throw new Error("callRecorderService not injected");
        }
        return this.callRecorderService?.pauseRecording(this);
    }

    /**
    * Resume the recording
    * @returns the call updated or an error
    */
    public async resumeRecording(): Promise<void> {
        if (!this.callRecorderService) {
            throw new Error("callRecorderService not injected");
        }
        return this.callRecorderService?.resumeRecording(this);
    }

    /**
    * Stop the recording
    * @returns the call updated or an error
    */
    public async stopRecording(): Promise<void> {
        if (!this.callRecorderService) {
            throw new Error("callRecorderService not injected");
        }
        return this.callRecorderService?.stopRecording(this);
    }

    /**
     * Cancel the recording
     * @returns the call updated or an error
     */
    public async cancelRecording(): Promise<void> {
        if (!this.callRecorderService) {
            throw new Error("callRecorderService not injected");
        }

        return this.callRecorderService?.cancelRecording(this);
    }
}
